import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Avatar,
  Paper,
  Stack,
  Divider,
  Button,
  useTheme,
} from "@mui/material";
import { motion } from "framer-motion";
import CodeIcon from "@mui/icons-material/Code";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";

const AboutSection = () => {
  const theme = useTheme();

  const values = [
    {
      icon: CodeIcon,
      title: "Technical Excellence",
      description:
        "Building reliable digital solutions with modern technologies that are easy to maintain and scale with your business.",
    },
    {
      icon: LightbulbIcon,
      title: "Creative Problem Solving",
      description:
        "Finding practical, innovative approaches to your business challenges through thoughtful technology and design.",
    },
    {
      icon: RocketLaunchIcon,
      title: "Local Growth Focus",
      description:
        "Developing digital solutions that strengthen your local presence and help you build lasting customer relationships.",
    },
  ];

  return (
    <Box
      sx={{
        py: { xs: 8, md: 12 },
        backgroundColor: "#fff",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={5}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true, margin: "-100px" }}
            >
              <Box
                sx={{
                  position: "relative",
                  borderRadius: "24px",
                  overflow: "hidden",
                  boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                  maxWidth: "500px", // Added to make image smaller
                  margin: "0 auto", // Center the smaller image
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    background:
                      "linear-gradient(135deg, rgba(82,113,255,0.2) 0%, rgba(255,145,77,0.2) 100%)",
                    zIndex: 1,
                  },
                }}
              >
                <Box
                  component="img"
                  // src="https://img.freepik.com/free-photo/portrait-white-man-isolated_53876-40306.jpg"
                  src="images/EricRakHero.png"
                  alt="Eric Rak"
                  sx={{
                    width: "100%",
                    height: "auto",
                    display: "block",
                    position: "relative",
                    zIndex: 0,
                  }}
                />
              </Box>
            </motion.div>
          </Grid>

          <Grid item xs={12} md={7}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true, margin: "-100px" }}
            >
              <Typography
                variant="overline"
                component="div"
                color="primary"
                fontWeight={600}
                sx={{ mb: 1, letterSpacing: 2 }}
              >
                ABOUT ME
              </Typography>
              <Typography
                variant="h3"
                component="h2"
                fontWeight={700}
                sx={{ mb: 3 }}
              >
                Building Digital Success for Small Businesses
              </Typography>

              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mb: 3, fontSize: "1.1rem", lineHeight: 1.7 }}
              >
                I'm Eric Rak, a full-stack developer with a passion for helping
                small local businesses thrive in the digital space. With
                experience across web development, mobile applications, cloud
                solutions, and digital marketing, I provide just-in-time technical
                solutions tailored to your specific business needs.
              </Typography>

              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mb: 4, fontSize: "1.1rem", lineHeight: 1.7 }}
              >
                My approach combines technical expertise with an understanding
                of small business dynamics, ensuring that every digital
                solution not only works flawlessly but also makes sense for your
                business goals. Whether you're a workshop owner, fitness
                trainer, or small business owner, I'm here to free up your time so you can 
                focus on your passion and the creative aspects of your business that inspired you 
                in the first place.
              </Typography>

              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={() => {
                  document
                    .getElementById("contact")
                    .scrollIntoView({ behavior: "smooth" });
                }}
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: "30px",
                  fontWeight: 600,
                }}
              >
                Let's Work Together
              </Button>
            </motion.div>
          </Grid>
        </Grid>

        <Divider sx={{ my: { xs: 6, md: 10 } }} />

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true, margin: "-100px" }}
        >
          <Typography
            variant="h4"
            component="h3"
            fontWeight={700}
            textAlign="center"
            sx={{ mb: 5 }}
          >
            My Core Values
          </Typography>
        </motion.div>

        <Grid container spacing={4}>
          {values.map((value, index) => (
            <Grid item xs={12} md={4} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true, margin: "-50px" }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    height: "100%",
                    borderRadius: "16px",
                    border: "1px solid rgba(230, 230, 230, 0.5)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      boxShadow: "0 8px 30px rgba(0, 0, 0, 0.12)",
                      transform: "translateY(-5px)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Box
                      sx={{
                        width: 50,
                        height: 50,
                        borderRadius: "10px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: `${theme.palette.primary.main}15`,
                        color: theme.palette.primary.main,
                        mr: 2,
                      }}
                    >
                      <value.icon sx={{ fontSize: 28 }} />
                    </Box>
                    <Typography variant="h6" fontWeight={600}>
                      {value.title}
                    </Typography>
                  </Box>
                  <Typography variant="body1" color="text.secondary">
                    {value.description}
                  </Typography>
                </Paper>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default AboutSection;
